import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MARK_TYPES, ZHE_NAMES, toToneMarks } from "@xinghai/shared";
import {
  Button,
  Card,
  EmptyState,
  Icon,
  Input,
  Select,
  Skeleton,
  Slider,
  Tag,
  useToast,
} from "@xinghai/ui";
import { ApiError, arbitrationApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import { AudioPanel } from "../annotator/components/AudioPanel";
import {
  DOMAIN_LABEL,
  FINAL_OPTIONS,
  INITIAL_OPTIONS,
  MARK_LABEL,
  TASK_LABEL_EXPERT,
  TONE_OPTIONS,
  conflictValueLabel,
  dimensionKind,
  dimensionLabel,
} from "../annotator/labels";
import type {
  ArbitrationConflict,
  ArbitrationDecidePayload,
  ArbitrationDetail,
} from "../annotator/types";

/** Stable identity for one conflict row. */
function rowKey(c: Pick<ArbitrationConflict, "char_index" | "dimension">): string {
  return `${c.char_index === null ? "u" : c.char_index}/${c.dimension}`;
}

interface CustomState {
  open: boolean;
  value: number | string | null;
}

/** docs/UX.md 10 — 仲裁面板. Experts are A/B only; never named (AUTHZ.md 双盲). */
export function ArbitrationPage() {
  const { recordingId = "" } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();

  const q = useQuery({
    queryKey: qk.arbitrationItem(recordingId),
    queryFn: () => arbitrationApi.get(recordingId),
    enabled: recordingId.length > 0,
  });
  const detail: ArbitrationDetail | undefined = q.data;

  const [notes, setNotes] = useState<Record<string, string>>({});
  const [custom, setCustom] = useState<Record<string, CustomState>>({});
  const [settled, setSettled] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: (vars: {
      conflict: ArbitrationConflict;
      payload: ArbitrationDecidePayload;
      label: string;
    }) => {
      setPending(rowKey(vars.conflict));
      return arbitrationApi.decide(
        recordingId,
        vars.conflict.char_index,
        vars.conflict.dimension,
        vars.payload,
      );
    },
    onSuccess: (res, vars) => {
      setPending(null);
      const key = rowKey(vars.conflict);
      setSettled((s) => ({ ...s, [key]: vars.label }));
      setCustom((c) => ({ ...c, [key]: { open: false, value: null } }));
      queryClient.setQueryData(
        qk.arbitrationItem(recordingId),
        (prev: ArbitrationDetail | undefined) =>
          prev ? { ...prev, conflicts: res.conflicts } : prev,
      );
      void queryClient.invalidateQueries({ queryKey: qk.arbitrationQueue });
      toast.success(
        res.remaining_conflicts === 0 ? "已裁定，这条录音的冲突全部处理完了。" : "已裁定。",
      );
    },
    onError: (err) => {
      setPending(null);
      toast.error(err instanceof ApiError ? err.message : "裁定没有保存，请重试。");
    },
  });

  // Group the open conflicts by character; utterance-level rows come last.
  const groups = useMemo(() => {
    const open = detail?.conflicts ?? [];
    const map = new Map<number | "u", ArbitrationConflict[]>();
    for (const c of open) {
      const k = c.char_index === null ? ("u" as const) : c.char_index;
      const list = map.get(k) ?? [];
      list.push(c);
      map.set(k, list);
    }
    return [...map.entries()].sort((a, b) => {
      if (a[0] === "u") return 1;
      if (b[0] === "u") return -1;
      return a[0] - b[0];
    });
  }, [detail]);

  if (q.isPending) {
    return (
      <div className="xz-container xz-stack" aria-busy="true">
        <Skeleton variant="rect" height={140} />
        <Skeleton variant="rect" height={280} />
      </div>
    );
  }

  if (q.isError || !detail) {
    return (
      <div className="xz-container">
        <EmptyState
          icon={<Icon id="ui-search" size={24} />}
          title="打不开这条仲裁"
          description="它可能已经完成或不在仲裁队列里了。"
          action={
            <Link className="gz-button gz-button--primary" to="/j">
              返回队列
            </Link>
          }
        />
      </div>
    );
  }

  const materialChars = detail.material?.chars ?? [];
  const totalOpen = detail.conflicts.length;

  const submitValue = (c: ArbitrationConflict, value: number | string | null, label: string) => {
    const key = rowKey(c);
    decide.mutate({
      conflict: c,
      payload: { decision: "value", value, note: notes[key]?.trim() || null },
      label,
    });
  };

  const submitSplit = (c: ArbitrationConflict) => {
    const key = rowKey(c);
    decide.mutate({
      conflict: c,
      payload: { decision: "split", value: null, note: notes[key]?.trim() || null },
      label: "分裂 → 掩码",
    });
  };

  const renderCustom = (c: ArbitrationConflict) => {
    const key = rowKey(c);
    const state = custom[key] ?? { open: false, value: null };
    if (!state.open) return null;
    const kind = dimensionKind(c.dimension);
    const setValue = (value: number | string | null) =>
      setCustom((m) => ({ ...m, [key]: { open: true, value } }));

    return (
      <div className="xz-arb__custom">
        {kind === "score" && (
          <Slider
            value={typeof state.value === "number" ? state.value : 1}
            label={`${dimensionLabel(c.dimension)} 自定值`}
            step={0.05}
            onChange={setValue}
          />
        )}
        {kind === "mark" && (
          <Select
            aria-label="自定标记类型"
            value={typeof state.value === "string" ? state.value : ""}
            onChange={(e) => setValue(e.target.value || null)}
          >
            <option value="">未选择</option>
            {MARK_TYPES.map((m) => (
              <option key={m} value={m}>
                {MARK_LABEL[m]}
              </option>
            ))}
          </Select>
        )}
        {kind === "phone" && (
          <Select
            aria-label="自定实现音素"
            value={typeof state.value === "string" ? state.value : ""}
            onChange={(e) => setValue(e.target.value || null)}
          >
            <option value="">未选择</option>
            <optgroup label="声母">
              {INITIAL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </optgroup>
            <optgroup label="韵母">
              {FINAL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </optgroup>
          </Select>
        )}
        {kind === "tone" && (
          <Select
            aria-label="自定实现调类"
            value={typeof state.value === "number" ? String(state.value) : ""}
            onChange={(e) => setValue(e.target.value === "" ? null : Number(e.target.value))}
          >
            <option value="">未选择</option>
            {TONE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
        )}
        {kind === "zhe" && (
          <Select
            aria-label="自定辙位"
            value={typeof state.value === "string" ? state.value : ""}
            onChange={(e) => setValue(e.target.value || null)}
          >
            <option value="">未选择</option>
            {ZHE_NAMES.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </Select>
        )}
        <Button
          variant="primary"
          size="sm"
          disabled={state.value === null || state.value === ""}
          onClick={() =>
            submitValue(c, state.value, `自定值 ${conflictValueLabel(c.dimension, state.value)}`)
          }
        >
          采用自定值
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCustom((m) => ({ ...m, [key]: { open: false, value: null } }))}
        >
          取消
        </Button>
      </div>
    );
  };

  return (
    <div className="xz-arb">
      <header className="xz-workbench__bar">
        <Link className="gz-button gz-button--quiet" to="/j">
          <Icon id="ui-arrow-left" size={20} /> 队列
        </Link>
        <span className="gz-type-data">{detail.recording.id}</span>
        <Tag tone="info">
          {TASK_LABEL_EXPERT[detail.recording.task]} · {DOMAIN_LABEL[detail.recording.domain]}
        </Tag>
        <span className="gz-caption">剩余冲突 {totalOpen}</span>
      </header>

      <div className="xz-arb__grid">
        <aside className="xz-workbench__audio">
          <AudioPanel src={detail.audio_url} label={`录音 ${detail.recording.id}`} />
          {detail.material && <p className="gz-caption">{detail.material.text}</p>}
          <p className="gz-caption">
            A / B 是两位专家的匿名代号，顺序对每条录音固定，不代表姓名或先后。
          </p>
        </aside>

        <main className="xz-arb__list" aria-label="冲突列表">
          {totalOpen === 0 ? (
            <EmptyState
              icon={
                <img
                  className="xz-mascot"
                  src="/design-system/avatar/assets/brand-motion/mascot-success.svg"
                  alt=""
                  aria-hidden="true"
                  width={160}
                  height={160}
                />
              }
              title="完成"
              description="这条录音的冲突已经全部裁定，状态已推进到 final。"
              action={
                <Link className="gz-button gz-button--primary" to="/j">
                  返回队列
                </Link>
              }
            />
          ) : (
            groups.map(([groupKey, rows]) => {
              const mc = groupKey === "u" ? null : materialChars[groupKey];
              const glyph = groupKey === "u" ? null : (rows[0]?.char ?? mc?.char ?? "?");
              return (
                <Card
                  key={String(groupKey)}
                  padding="md"
                  className="xz-arb__group"
                  title={
                    groupKey === "u" ? (
                      "句级"
                    ) : (
                      <span className="xz-arb__glyph">
                        <span className="xz-arb__char">{glyph}</span>
                        {mc?.pinyin_locked && (
                          <span className="xz-arb__pinyin">{toToneMarks(mc.pinyin_locked)}</span>
                        )}
                        <span className="gz-caption">第 {groupKey + 1} 字</span>
                      </span>
                    )
                  }
                >
                  <ul className="xz-arb__rows">
                    {rows.map((c) => {
                      const key = rowKey(c);
                      const done = settled[key];
                      const busy = pending === key && decide.isPending;
                      return (
                        <li key={key} className="xz-arb__row">
                          <div className="xz-arb__meta">
                            <span className="xz-arb__dim">{dimensionLabel(c.dimension)}</span>
                            <span className="xz-arb__ab">
                              <span>
                                A <b>{conflictValueLabel(c.dimension, c.a)}</b>
                              </span>
                              <span>
                                B <b>{conflictValueLabel(c.dimension, c.b)}</b>
                              </span>
                              {c.delta != null && (
                                <span className="gz-caption">Δ {c.delta.toFixed(2)}</span>
                              )}
                            </span>
                          </div>

                          {done ? (
                            <p className="xz-arb__done" role="status">
                              <Icon id="ui-check-circle" size={20} /> 已裁定 · {done}
                            </p>
                          ) : (
                            <>
                              <div className="xz-arb__actions">
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  loading={busy}
                                  onClick={() =>
                                    submitValue(
                                      c,
                                      c.a,
                                      `采用 A（${conflictValueLabel(c.dimension, c.a)}）`,
                                    )
                                  }
                                >
                                  采用A
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  loading={busy}
                                  onClick={() =>
                                    submitValue(
                                      c,
                                      c.b,
                                      `采用 B（${conflictValueLabel(c.dimension, c.b)}）`,
                                    )
                                  }
                                >
                                  采用B
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    setCustom((m) => ({
                                      ...m,
                                      [key]: { open: !(m[key]?.open ?? false), value: null },
                                    }))
                                  }
                                >
                                  自定值
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  loading={busy}
                                  onClick={() => submitSplit(c)}
                                >
                                  分裂→掩码
                                </Button>
                              </div>
                              <p className="gz-caption">掩码表示该项不进入训练数据。</p>
                              {renderCustom(c)}
                              <Input
                                value={notes[key] ?? ""}
                                placeholder="备注（可选）"
                                aria-label={`${dimensionLabel(c.dimension)} 裁定备注`}
                                onChange={(e) => setNotes((n) => ({ ...n, [key]: e.target.value }))}
                              />
                            </>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </Card>
              );
            })
          )}
        </main>
      </div>
    </div>
  );
}
